import type { OrderDetail, OrderItem } from "@probetalab/contracts";

/**
 * Rules of the order page's edit actions, mirrored from the API handlers (`order_item.cancel`,
 * `order.update_header` in apps/api/src/commands/commerce.ts) so the sheet explains a refusal
 * before anyone taps. The server stays the authority; these only decide what the screen offers.
 */

export function orderIsOpen(o: Pick<OrderDetail, "state">): boolean {
  return o.state !== "cancelled" && o.state !== "closed";
}

/** Why a study cannot be removed, in the words the sheet shows; null when it can. */
export function removalBlock(o: OrderDetail, item: OrderItem): string | null {
  if (!orderIsOpen(o)) return "La solicitud está cerrada o cancelada.";
  if (item.state === "cancelled") return "El estudio ya está cancelado.";
  if (item.state === "validated" || item.state === "issued" || item.state === "amended") {
    return "Este estudio ya tiene resultados validados. Ya no se puede quitar; si hay un error en el resultado, se corrige con una enmienda.";
  }
  if (item.state === "captured" || item.has_results) {
    return "Este estudio ya tiene resultados capturados, así que ya no se puede quitar de la solicitud.";
  }
  if (o.items.filter((i) => i.state !== "cancelled").length <= 1) {
    return "Es el único estudio de la solicitud. Para quitarlo hay que cancelar la solicitud completa.";
  }
  return null;
}

export interface RemovalPreview {
  total_cents: number;
  /** Money already paid beyond the new total: the patient's saldo a favor. Never refunded here. */
  credit_cents: number;
  balance_cents: number;
  /** Label of the tube that stops being pending because this was its only live study. */
  tube_released: string | null;
}

/** What the order looks like after removing `item`, computed the way the handler does. */
export function previewRemoval(o: OrderDetail, item: OrderItem): RemovalPreview {
  const subtotal = o.subtotal_cents - item.price_cents;
  let discount = o.discount_cents;
  if (o.discount?.kind === "percent") discount = Math.round((subtotal * o.discount.value) / 100);
  if (discount > subtotal) discount = subtotal;
  const total = subtotal - discount;
  const tube = item.sample_label_code ? o.samples.find((s) => s.label_code === item.sample_label_code) : undefined;
  const othersInTube = o.items.filter((i) => i.id !== item.id && i.state !== "cancelled" && i.sample_label_code === item.sample_label_code).length;
  return {
    total_cents: total,
    credit_cents: Math.max(0, o.paid_cents - total),
    balance_cents: Math.max(0, total - o.paid_cents),
    tube_released: tube && tube.state === "pending" && othersInTube === 0 ? tube.label_code : null,
  };
}

/** Saldo a favor of an order as it stands: paid beyond the total (e.g. after removing a paid study). */
export function orderCredit(o: Pick<OrderDetail, "paid_cents" | "total_cents">): number {
  return Math.max(0, o.paid_cents - o.total_cents);
}

/** The condition selects reference intervals: it is frozen once any live study has a value. */
export function conditionLocked(o: OrderDetail): boolean {
  return o.items.some((i) => i.state !== "cancelled" && (i.state !== "pending" || i.has_results));
}

export const REMOVAL_REASONS = ["Se agregó por error", "El paciente ya no lo quiere"] as const;
